import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import "./ToolTip.css";

interface TooltipProps {
  ClassName?: string;
  text: string;
  delay?: number;
  children: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({ ClassName, text, delay = 4000, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const updateTooltipPosition = () => {
    if (targetRef.current && tooltipRef.current) {
      const targetRect = targetRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      let top = targetRect.top + window.scrollY - tooltipRect.height - 10; // Above element
      let left = targetRect.left + window.scrollX + (targetRect.width / 2) - (tooltipRect.width / 2);

      // If no space above, place below
      if (top < 10) {
        top = targetRect.bottom + window.scrollY + 10;
      }

      // Ensure tooltip stays within screen bounds
      if (left < 10) left = 10;
      if (left + tooltipRect.width > window.innerWidth) {
        left = window.innerWidth - tooltipRect.width - 10;
      }

      setPosition({ top, left });
    }
  };

  const showTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (fadeOutTimeoutRef.current) clearTimeout(fadeOutTimeoutRef.current);

    setIsFadingOut(false); // Reset fade-out
    setIsVisible(true);
    updateTooltipPosition();

    timeoutRef.current = setTimeout(() => {
      setIsFadingOut(true); // Start fade-out animation
      fadeOutTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 1500); // Delay hiding after fade-out animation
    }, delay);
  };

  useEffect(() => {
    if (isVisible) {
      updateTooltipPosition(); // Adjust position immediately
      window.addEventListener("scroll", updateTooltipPosition);
      window.addEventListener("resize", updateTooltipPosition);
    }

    return () => {
      window.removeEventListener("scroll", updateTooltipPosition);
      window.removeEventListener("resize", updateTooltipPosition);
    };
  }, [isVisible]);

  return (
    <div
      ref={targetRef}
      onMouseEnter={showTooltip}
      onMouseLeave={() => setIsFadingOut(true)}
      className={`d-inline-block position-relative ${ClassName}`}
      onClick={showTooltip}
    >
      {children}
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`tool-tip-strip rounded ${isFadingOut ? "fade-out" : "fade-in"}`}
            style={{
              position: "absolute",
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
          >
            {text}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
